import { FilterTab } from "components/DesignSystem/FilterTab/FilterTab";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { CompanyTaskCountOverview } from "types/Models/task";
import { TaskListFilterType } from "./TaskList";
import {
  CLICKED_ALL_ON_FILINGS,
  CLICKED_COMPLETED_ON_FILINGS,
  CLICKED_ONGOING_ON_FILINGS,
  CLICKED_PAYMENT_PENDING_ON_ME_ON_FILINGS,
} from "constants/analyticsEvents";
import {
  FilingStatusType,
  FILTER_FILING_ADD_DETAILS,
  FILTER_FILING_ALL,
  FILTER_FILING_ARCHIVED,
  FILTER_FILING_COMPLETED,
  FILTER_FILING_ONGOING,
  FILTER_FILING_PAYMENT_PENDING,
  FILTER_FILING_PROCESSING,
  FILTER_FILING_STATUS,
  FILTER_HIGHLIGHT_TASK_ALARM_UUID,
} from "constants/task";
import { useAnalytics } from "hooks/useAnalytics";
import { PayForAllRequiredFilings } from "./PayForAllRequiredFilings";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useUpdateQuery } from "hooks/useQuery";

export const CompanyTaskOverview = ({
  allSeasons,
  taskCounts,
  isFetching,
  values,
  updateFilter,
}: {
  isFetching: boolean;
  taskCounts?: CompanyTaskCountOverview;
  allSeasons: string;
} & TaskListFilterType) => {
  const { isAdmin } = useRoleBasedView();
  const { trackEvent } = useAnalytics();
  const { update } = useUpdateQuery();
  const {
    all = 0,
    payment_pending = 0,
    add_details = 0,
    processing = 0,
    completed = 0,
    archived = 0,
  } = taskCounts || {};

  const ongoing = processing + add_details;

  const handleFilterClick = (filterValue: FilingStatusType) => {
    updateFilter(FILTER_FILING_STATUS, filterValue);
    update({ query: FILTER_HIGHLIGHT_TASK_ALARM_UUID, value: null });
  };

  const handleAllClick = () => {
    trackEvent(CLICKED_ALL_ON_FILINGS);
    handleFilterClick(FILTER_FILING_ALL);
  };

  const handleCompletedClick = () => {
    trackEvent(CLICKED_COMPLETED_ON_FILINGS);
    handleFilterClick(FILTER_FILING_COMPLETED);
  };

  const handleOngoingClick = () => {
    trackEvent(CLICKED_ONGOING_ON_FILINGS);
    handleFilterClick(FILTER_FILING_ONGOING);
  };

  const handlePaymentPendingClick = () => {
    trackEvent(CLICKED_PAYMENT_PENDING_ON_ME_ON_FILINGS);
    handleFilterClick(FILTER_FILING_PAYMENT_PENDING);
  };

  return (
    <div className="hide-scrollbar t-flex t-flex-wrap t-gap-2 t-justify-between sm:t-flex-nowrap sm:t-justify-start sm:t-overflow-x-auto">
      <FilterTab
        isSelected={Boolean(values.FILING_STATUS === FILTER_FILING_ALL)}
        onClick={handleAllClick}
        classNames="t-flex-1 sm:t-flex-none sm:t-whitespace-nowrap sm:t-px-3 sm:t-py-2"
      >
        <div className="t-space-y-1 sm:t-flex sm:t-items-center sm:t-justify-between sm:t-gap-2 sm:t-space-y-0">
          <div className="t-text-subtext-sm t-text-text-30">All</div>
          <div className="t-text-subtext t-text-text-100 sm:t-text-subtext-sm">
            {isFetching ? (
              <Skeleton className="t-h-6 t-w-16 t-rounded-md sm:t-h-4 sm:t-w-8" />
            ) : (
              all
            )}
          </div>
        </div>
      </FilterTab>
      <FilterTab
        isSelected={Boolean(values.FILING_STATUS === FILTER_FILING_COMPLETED)}
        onClick={handleCompletedClick}
        classNames="t-flex-1 sm:t-flex-none sm:t-whitespace-nowrap sm:t-px-3 sm:t-py-2"
      >
        <div className="t-space-y-1 sm:t-flex sm:t-items-center sm:t-justify-between sm:t-gap-2 sm:t-space-y-0">
          <div className="t-text-subtext-sm t-text-text-30">Completed</div>
          <div className="t-text-subtext t-text-text-100 sm:t-text-subtext-sm">
            {isFetching ? (
              <Skeleton className="t-h-6 t-w-16 t-rounded-md sm:t-h-4 sm:t-w-8" />
            ) : (
              completed
            )}
          </div>
        </div>
      </FilterTab>
      <FilterTab
        isSelected={Boolean(values.FILING_STATUS === FILTER_FILING_ONGOING)}
        onClick={handleOngoingClick}
        classNames="t-flex-1 sm:t-flex-none sm:t-whitespace-nowrap sm:t-px-3 sm:t-py-2"
      >
        <div className="t-space-y-1 sm:t-flex sm:t-items-center sm:t-justify-between sm:t-gap-2 sm:t-space-y-0">
          <div className="t-text-subtext-sm t-text-text-30">Ongoing</div>
          <div className="t-text-subtext t-text-text-100 sm:t-text-subtext-sm">
            {isFetching ? (
              <Skeleton className="t-h-6 t-w-16 t-rounded-md sm:t-h-4 sm:t-w-8" />
            ) : (
              ongoing
            )}
          </div>
        </div>
      </FilterTab>
      {isAdmin && (
        <FilterTab
          isSelected={Boolean(values.FILING_STATUS === FILTER_FILING_ARCHIVED)}
          onClick={() => handleFilterClick(FILTER_FILING_ARCHIVED)}
          classNames="t-flex-1 sm:t-flex-none sm:t-whitespace-nowrap sm:t-px-3 sm:t-py-2"
        >
          <div className="t-space-y-1 sm:t-flex sm:t-items-center sm:t-justify-between sm:t-gap-2 sm:t-space-y-0">
            <div className="t-text-subtext-sm t-text-text-30">Inactive</div>
            <div className="t-text-subtext t-text-text-100 sm:t-text-subtext-sm">
              {isFetching ? (
                <Skeleton className="t-h-6 t-w-16 t-rounded-md sm:t-h-4 sm:t-w-8" />
              ) : (
                archived
              )}
            </div>
          </div>
        </FilterTab>
      )}
      <PayForAllRequiredFilings
        allSeasons={allSeasons}
        isSelected={Boolean(
          values.FILING_STATUS === FILTER_FILING_PAYMENT_PENDING
        )}
        handleClick={handlePaymentPendingClick}
        isFetching={isFetching}
        paymentPendingCount={payment_pending}
      />
    </div>
  );
};
